import React from 'react';
import { Crown, Clock, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { SUBSCRIPTION_ENABLED } from '../../config/monetization';

interface TrialBannerProps {
  onUpgradeClick?: () => void;
  showWhenPremium?: boolean;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({
  onUpgradeClick,
  showWhenPremium = false
}) => {
  const {
    isPremiumUser,
    isTrialActive,
    isTrialExpired,
    daysRemaining,
    getSubscriptionStatusMessage
  } = useSubscription();

  // Freemium mode: subscription UI is hidden entirely.
  if (!SUBSCRIPTION_ENABLED) {
    return null;
  }

  // Debug logging for production to help identify the issue
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 TrialBanner Debug:', {
      isPremiumUser,
      isTrialActive,
      isTrialExpired,
      daysRemaining,
      showWhenPremium
    });
  }

  // Removed diagnostic banner - subscription logic is now working correctly

  // Don't show banner if user is premium (unless explicitly requested)
  if (isPremiumUser && !showWhenPremium) {
    return null;
  }

  // Don't show banner for new users who haven't started trial
  if (!isPremiumUser && !isTrialActive && !isTrialExpired) {
    return null;
  }
  
  const getBannerStyle = () => {
    if (isPremiumUser) {
      return 'bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-gray-800 border-2 border-sacred-gold-muted';
    }

    if (isTrialExpired) {
      return 'bg-gradient-to-r from-sacred-rose-muted to-sacred-amber-muted text-white border-2 border-sacred-rose-muted';
    }

    if (daysRemaining <= 1) {
      return 'bg-gradient-to-r from-sacred-amber-muted to-sacred-rose-muted text-white border-2 border-sacred-amber-muted';
    }

    return 'bg-gradient-to-r from-sacred-blue-muted to-sacred-navy-light text-white border-2 border-sacred-gold-muted';
  };
  
  const getIcon = () => {
    if (isPremiumUser) {
      return <Crown className="w-4 h-4" />;
    }
    
    if (isTrialExpired || daysRemaining <= 1) {
      return <AlertTriangle className="w-4 h-4" />;
    }
    
    return <Clock className="w-4 h-4" />;
  };
  
  const getActionText = () => {
    if (isPremiumUser) {
      return null;
    }
    
    if (isTrialExpired) {
      return 'Upgrade Now';
    }
    
    if (daysRemaining <= 2) {
      return 'Upgrade Now';
    }
    
    return 'Learn More';
  };
  
  return (
    <div className={`${getBannerStyle()} px-4 py-3 rounded-lg shadow-lg mb-4 transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getIcon()}
          <span className="text-sm font-medium">
            {getSubscriptionStatusMessage()}
          </span>
        </div>
        
        {onUpgradeClick && getActionText() && (
          <button
            onClick={onUpgradeClick}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200"
          >
            {getActionText()}
          </button>
        )}
      </div>
    </div>
  );
};